import { useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { rememberMyIpProjectHandoff } from '$/domains/myIp/projectHandoff';
import { createMyIpProjectAction } from '$/domains/myIp/createProjectAction';
import { getMyIpCreationRoom } from '$/domains/myIp/myIpService';
import type { MyIpItem } from '$/domains/myIp/types';
import { navigate } from '@/common/agents';

/** 同一个 IP 的失败重试复用项目与幂等键，卸载或账号/空间切换废弃旧导航。 */
export function useMyIpProjectAction(scopeKey: string, prefix: string) {
  const mounted = useRef(false);
  const current = useRef({ scopeKey, prefix, epoch: 0 });
  const actions = useRef(new Map<string, ReturnType<typeof createMyIpProjectAction>>());
  if (current.current.scopeKey !== scopeKey || current.current.prefix !== prefix) {
    current.current = { scopeKey, prefix, epoch: current.current.epoch + 1 };
    actions.current = new Map();
  }
  const context = current.current;
  /** 页面卸载后，迟到的创建响应不能再次触发跳转。 */
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  /** V3 新项目按服务端返回的真实房间进入 Team Studio。 */
  return async (item: MyIpItem): Promise<void> => {
    if (!mounted.current || context !== current.current) throw new Error('IP action context expired');
    let action = actions.current.get(item.ipId);
    if (!action) {
      action = createMyIpProjectAction({
        ipId: item.ipId,
        createKey: uuid,
        isCurrent: () => mounted.current && context === current.current,
        /** 房间查询完成后再次检查来源页面，避免迟到导航。 */
        navigate: async (result) => {
          const room = await getMyIpCreationRoom(result);
          if (!mounted.current || context !== current.current) throw new Error('IP action context expired');
          if (!['agent', 'workflow'].includes(room.roomType)) throw new Error('暂不支持该项目的编辑器类型');
          rememberMyIpProjectHandoff(room.projectId, room.roomId, item.ipId, scopeKey);
          const query = new URLSearchParams({ room_id: room.roomId, project_id: room.projectId, initial_ip_id: item.ipId });
          return navigate({ mode: 'url', url: `${prefix}/team_studio?${query}` });
        },
      });
      actions.current.set(item.ipId, action);
    }
    await action.run();
  };
}
